import { useMemo } from 'react';

import { styled } from '@mui/material';

import { Typography } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';

import { useReportContext } from '../../../../../../context/useReportContext';

const StyledExplanation = styled(Typography)({
  fontSize: 15,
  fontWeight: 500,
});

export const Explanation = () => {
  const { t } = useI18n();
  const { reportConfig, statistics, isLoading } = useReportContext();
  const { config } = reportConfig;

  const { metrics, meetsRequirements, simulatedReviews, humanReviews } =
    statistics;

  const toReduce = useMemo(() => {
    const allToReduce: string[] = [];

    if (!config) return [];

    const { dropOffs, missClicks, avgDuration } = metrics;
    if (dropOffs * 100 > config.dropOffRate)
      allToReduce.push(t('prototypes:report.metric.drop_off_rate'));
    if (missClicks * 100 > config.missclickRate)
      allToReduce.push(t('prototypes:report.metric.missclick_rate'));
    if (avgDuration > config.timeToSuccess)
      allToReduce.push(t('prototypes:report.metric.average_duration'));

    return allToReduce;
  }, [meetsRequirements, metrics, config, t]);

  const toIncrease = useMemo(() => {
    if (!config) return [];
    const allToIncrease: string[] = [];

    const { successRatio } = metrics;

    const simulatedReviewsTotal =
      simulatedReviews.failed + simulatedReviews.succeded;
    const humanReviewsTotal = humanReviews
      ? humanReviews.failed + humanReviews.succeded
      : 0;

    if (successRatio * 100 < config.successRate)
      allToIncrease.push(t('prototypes:report.metric.success_rate'));
    if (humanReviewsTotal < config.minimumHumanParticipants)
      allToIncrease.push(
        t('prototypes:report.test_participants.human_participants')
      );
    if (simulatedReviewsTotal < config.minimumAiParticipants)
      allToIncrease.push(
        t('prototypes:report.test_participants.ai_simulated_participants')
      );

    return allToIncrease;
  }, [meetsRequirements, metrics, humanReviews, simulatedReviews, config, t]);

  if (meetsRequirements || isLoading) return null;

  return (
    <StyledExplanation>
      {t('prototypes:report.decision.for_a_yes')}
      {toReduce.length > 0 ? (
        <div>
          {t('prototypes:report.decision.reduce', {
            list: toReduce.join(', '),
          })}
        </div>
      ) : null}
      {toIncrease.length > 0 ? (
        <div>
          {t('prototypes:report.decision.increase', {
            list: toIncrease.join(', '),
          })}
        </div>
      ) : null}
    </StyledExplanation>
  );
};
